import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {NON_STANDARD_AMINO_ACIDS} from 'biojs/Codons';
import {containsNonStandardAminoAcids} from 'biojs/Util';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {getApolloClient} from 'graphql-client/ApolloClient';
import * as ApolloWrappers from 'graphql-client/ApolloWrappers';
import {ProteinStructureExternalFile, ProteinVersion} from 'molbio/proteins/util/ProteinConstants';
import {
  STRUCTURE_PREDICTION_MODEL,
  getMaxAminoAcidCount,
  getModelDisplayName,
} from 'molbio/proteins/util/StructurePredictionModels';

import {RemainingExecutionsAndTenantNameQuery} from './__generated/RemainingExecutionsAndTenantNameQuery';
import * as AlphaFoldDAO from './AlphaFoldDAO';

export async function requestRun(
  proteinIds: Array<string>,
  moleculeIds: Array<string>,
  primaryProteinId: string,
  remainingExecutionsAndTenantNameData: RemainingExecutionsAndTenantNameQuery,
  selectedModel: string
): Promise<void> {
  if (BenchlingConfig.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS) {
    await AlphaFoldDAO.runStructurePrediction(proteinIds, moleculeIds, selectedModel);
  } else {
    if (proteinIds.length > 1) {
      throw new Error(
        `Multimers not yet supported for ${getModelDisplayName(STRUCTURE_PREDICTION_MODEL.ALPHAFOLD)}`
      );
    }
    assert(primaryProteinId === proteinIds[0], 'Expected primary protein to match the protein in the list');
    await AlphaFoldDAO.runAlphaFold(proteinIds[0]);
  }

  // Update Apollo cache to reflect the new prediction in progress
  ApolloWrappers.writeQuery(
    {query: REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY},
    {
      ...remainingExecutionsAndTenantNameData,
      remainingAlphafoldExecutions: remainingExecutionsAndTenantNameData.remainingAlphafoldExecutions - 1,
    }
  );

  // Since the list of in progress predictions is not just a boolean,
  // we need to evict the list because we do not have all of the information here to update the cache locally
  getApolloClient().cache.evict({
    id: `ProteinNode:${primaryProteinId}`,
    fieldName: 'inProgressStructurePredictionRuns',
  });
}

interface UnavailableStructureInfo {
  structureFile: null;
  model: null;
  modelVersion: null;
  structureFileOutOfDate: true;
  versionId: null;
  coordinateStructureId: null;
  createdAt: null;
}

const UNAVAILABLE_STRUCTURE_INFO: UnavailableStructureInfo = {
  structureFile: null,
  model: null,
  structureFileOutOfDate: true,
  modelVersion: null,
  versionId: null,
  coordinateStructureId: null,
  createdAt: null,
};

export interface AvailableStructureInfo {
  structureFile: ProteinStructureExternalFile;
  model: string;
  modelVersion: string;
  structureFileOutOfDate: boolean;
  versionId: number;
  createdAt: string;
  coordinateStructureId: string;
  componentIds: Array<string>;
}

/**
 * Utility to evaluate info about a Protein's AlphaFold structure files.
 *
 * structureFile: the most recent completed structure file available, or undefined if there are none.
 * structureFileOutOfDate: indicates whether structureFile belongs to the most recent protein version.
 */
export function getStructureInfoFromVersions(
  currentlyViewedProteinVersionId: number,
  versions?: Array<ProteinVersion>
): UnavailableStructureInfo | AvailableStructureInfo {
  if (versions === undefined || versions.length === 0) {
    return UNAVAILABLE_STRUCTURE_INFO;
  }
  const currentVersion = versions.find((version) => version.id === currentlyViewedProteinVersionId);

  // Return null values if the requested version doesn't exist
  if (!currentVersion) {
    return UNAVAILABLE_STRUCTURE_INFO;
  }

  const currentVersionStructures = currentVersion.structures;
  if (currentVersionStructures && currentVersionStructures.length) {
    const versionId = currentVersion.id;
    const structure = currentVersionStructures[0];
    return {
      structureFile: structure.externalFile,
      model: structure.model,
      modelVersion: structure.modelVersion,
      structureFileOutOfDate: false,
      versionId,
      coordinateStructureId: structure.id,
      createdAt: structure.createdAt,
      componentIds: structure.componentIds,
    };
  }

  const flattenedVersionStructures = versions.flatMap((version) =>
    version.structures.map((structure) => ({
      structure,
      versionId: version.id,
      model: structure.model,
      modelVersion: structure.modelVersion,
    }))
  );
  if (flattenedVersionStructures.length) {
    const {versionId, structure, model, modelVersion} =
      flattenedVersionStructures[flattenedVersionStructures.length - 1];
    return {
      structureFile: structure.externalFile,
      model,
      modelVersion,
      structureFileOutOfDate: true,
      versionId,
      coordinateStructureId: structure.id,
      createdAt: structure.createdAt,
      componentIds: structure.componentIds,
    };
  }
  return UNAVAILABLE_STRUCTURE_INFO;
}

export async function downloadStructureFile(
  structureFile: ProteinStructureExternalFile,
  proteinId: string,
  versionId: number
): Promise<void> {
  const signedURL = await performSignedFileQuery(structureFile.id, structureFile.url, proteinId, versionId);
  if (signedURL == null) {
    return;
  }
  window.open(signedURL, '_blank', 'noopener noreferrer');
  trackEvent(MOLBIO_ANALYTICS_ACTIONS.DOWNLOADED_ALPHAFOLD_STRUCTURE);
}

export function getCoordinateStructureId(coordinateStructure: AvailableStructureInfo): string {
  return coordinateStructure.structureFile.id;
}

export function getStructuresForVersion(
  versions?: Array<ProteinVersion>,
  currentVersionId?: number
): Array<AvailableStructureInfo> {
  if (!versions || versions.length === 0) {
    return [];
  }

  // If currentVersionId is provided, only return predictions from that version
  // Otherwise, return predictions from all versions (backward compatibility)
  const versionsToProcess = currentVersionId
    ? versions.filter((version) => version.id === currentVersionId)
    : versions;

  const allCoordinateStructures: Array<AvailableStructureInfo> = [];
  const fallbackCurrentVersionId = versions[versions.length - 1].id;

  // Iterate through filtered versions and collect their structures
  versionsToProcess.forEach((version) => {
    version.structures.forEach((structure) => {
      allCoordinateStructures.push({
        structureFile: structure.externalFile,
        model: structure.model,
        modelVersion: structure.modelVersion,
        versionId: version.id,
        coordinateStructureId: structure.id,
        createdAt: structure.createdAt,
        structureFileOutOfDate: version.id !== (currentVersionId || fallbackCurrentVersionId),
        componentIds: structure.componentIds,
      });
    });
  });

  // Sort by creation date in reverse chronological order (newest first)
  return allCoordinateStructures.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export function findCoordinateStructureById(
  coordinateStructures: Array<AvailableStructureInfo>,
  coordinateStructureId: string
): AvailableStructureInfo | null {
  return coordinateStructures.find((p) => getCoordinateStructureId(p) === coordinateStructureId) || null;
}

export const REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY: DocumentNode = gql`
  query RemainingExecutionsAndTenantNameQuery @skipAudit @engComponent(name: MOLBIO) {
    remainingAlphafoldExecutions
    currentTenant {
      ... on TenantNode {
        id
        displayName
      }
    }
  }
`;

export const USER_VERIFICATION_STATUS: DocumentNode = gql`
  query UserVerificationStatusQuery @skipAudit @engComponent(name: MOLBIO) {
    currentUser {
      ... on UserNode {
        id
        institution {
          id
          verified
        }
        verified
      }
    }
  }
`;

const INSUFFICIENT_PERMISSIONS_MESSAGE =
  'You do not have sufficient permissions to request a structure for this AA sequence.';
const NON_STANDARD_RESIDUES_MESSAGE = `This protein contains at least one non-standard amino acid (${NON_STANDARD_AMINO_ACIDS.join(
  ', '
)}) which cannot produce a valid structure prediction.`;
const NO_RESIDUES_MESSAGE =
  'You cannot run a structure prediction on this AA sequence since it does not contain any residues.';

export function getRunBlockingMessage(
  aminoAcids: string,
  permissions: Permissions,
  remainingRuns: number,
  model: string
): string | null {
  if (remainingRuns <= 0) {
    return 'You do not have any structure prediction runs available currently.';
  } else if (!permissions.polymerFeaturesEditable) {
    return INSUFFICIENT_PERMISSIONS_MESSAGE;
  } else if (containsNonStandardAminoAcids(aminoAcids)) {
    return NON_STANDARD_RESIDUES_MESSAGE;
  } else if (aminoAcids.length > getMaxAminoAcidCount(model)) {
    return `At the moment, you cannot request structures for sequences with over ${getMaxAminoAcidCount(model)} residues.`;
  } else if (aminoAcids.length === 0) {
    return NO_RESIDUES_MESSAGE;
  } else {
    return null;
  }
}
